import React, { useEffect, useState } from 'react';
import LoadingSpinner from '@/components/ui/loading-spinner';
import { type PreviewType } from './DocumentPreviewModal';

const TextPreview: React.FC<{ viewUrl: string; open: boolean }> = ({
  viewUrl,
  open,
}) => {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setContent(null);
    setError(false);
    fetch(viewUrl)
      .then(r => {
        if (!r.ok) throw new Error();
        return r.text();
      })
      .then(setContent)
      .catch(() => setError(true));
  }, [viewUrl, open]);

  if (error) {
    return (
      <p className="text-red-500 text-sm">Failed to load file contents.</p>
    );
  }
  if (content === null) return <LoadingSpinner />;
  return (
    <pre className="whitespace-pre-wrap text-sm font-mono text-gray-800 w-full">
      {content}
    </pre>
  );
};

interface PreviewContentProps {
  previewType: PreviewType;
  viewUrl: string;
  fileName: string;
  open: boolean;
}

const PreviewContent: React.FC<PreviewContentProps> = ({
  previewType,
  viewUrl,
  fileName,
  open,
}) => {
  switch (previewType) {
    case 'image':
      return (
        <img
          src={viewUrl}
          alt={fileName}
          className="max-w-full max-h-full object-contain"
        />
      );
    case 'pdf':
      return (
        <iframe
          src={viewUrl}
          title={fileName}
          className="w-full h-[80vh] border-0"
        />
      );
    case 'text':
      return <TextPreview viewUrl={viewUrl} open={open} />;
    case 'video':
      return (
        <video controls src={viewUrl} className="max-w-full max-h-[80vh]">
          Your browser does not support video playback.
        </video>
      );
    case 'audio':
      return <audio controls src={viewUrl} className="w-full" />;
  }
};

export default PreviewContent;
